import { useMemo } from "react";
import { bool, object, number, string } from "prop-types";
import { observer } from "mobx-react";
import { Tables, hashCode } from "@repo/utils";
import { chunk } from "lodash";
import { ELEMENT_TYPES, getTimelineChartSegments } from "@utils";
import {
  useEstimateStructure,
  useEstimateEditorSettings,
  Row,
  RowGroup,
  TimelineValueCell, useVisibilityModeCellClassName
} from "@tools";
import { TimelineFiller } from "../TimelineFiller";
import { TitleCell } from "./components/TitleCell";
import classnames from "classnames";

export const TimelineNestedRow = observer(({
  resource,
  isLastGroupRow,
  tableDepth,
  turnOff,
  parentColor,
  parentUsesTimelinePercent
}) => {
  const structure = useEstimateStructure();
  const { allowEdition } = useEstimateEditorSettings();
  
  const { modifier } = structure.settings;

  const {
    workTypeId,
    treePath,
    presentedTimeline,
    maxTimelineHours,
    timelineStartMonth,
    parsedMta,
    usesTimelinePercent,
    isHidden,
    hideOnTimeline,
  } = resource;
  
  const className = useVisibilityModeCellClassName(isHidden || hideOnTimeline);
  
  const maxHours = useMemo(() => (
    maxTimelineHours / modifier
  ), [maxTimelineHours, modifier]);
  
  const chunkedTimeline = chunk(presentedTimeline?.weeks || [], 4).slice(0, resource.totalDevelopmentTime);
  // const chunkedTimeline = {
  //   months: chunk(presentedTimeline?.weeks || [], 4),
  //   startMonth: presentedTimeline?.startMonth || 0,
  //   startWeek: presentedTimeline?.startWeek || 0,
  // };
  
  const timelineChartSegments = getTimelineChartSegments(
    presentedTimeline,
    parsedMta[structure.timelineWorkTypeHoursKey(workTypeId)],
    modifier,
    usesTimelinePercent
  );
  
  const hashedPath = useMemo(() => (
    [Tables.TIMELINE, ...treePath].map((id) => hashCode(id))
  ), [treePath]);
  
  return (
    <RowGroup
      tableDepth={tableDepth}
      isStatic//={!allowEdition}
      hashedPath={hashedPath}
      elementType={ELEMENT_TYPES.TIMELINE_RESOURCE}
      className={classnames("timeline", className)}
    >
      <Row
        useDragHandleCell={allowEdition}
        isLastGroupRow={isLastGroupRow}
        tableDepth={tableDepth}
        visible={!turnOff}
        presetClass="preset-rowTimeline"
        actionPresetClass="preset-paperTimeline"
      >
        <TitleCell resource={resource} tableDepth={tableDepth} />
        {
          chunkedTimeline.map((month, i) => (
            <TimelineValueCell
              key={`month${i}`}
              monthIndex={i}
              month={month}
              maxHours={maxHours}
              timelineStarted={!timelineStartMonth || i >= timelineStartMonth }
              allowEdition={ allowEdition && !usesTimelinePercent }
              element={resource}
              color={parentColor}
              timelineChartSegments={timelineChartSegments}
              readOnly={parentUsesTimelinePercent}
            />
          ))
        }
        <TimelineFiller totalDevelopmentTime={chunkedTimeline.length} />
      </Row>
    </RowGroup>
  );
});

TimelineNestedRow.propTypes = {
  resource: object.isRequired,
  tableDepth: number.isRequired,
  isLastGroupRow: bool,
  turnOff: bool,
  parentColor: string,
  parentUsesTimelinePercent: bool,
};
